// multiconnection.ts — the five-route instrument, ported from the design attachment's
// iframe-1 inline script (lines 2743-2808) plus its CTA bridge (2809-2818).
//
// The source ran inside `<iframe srcdoc>`, so it owned a whole document: it reached elements
// with `document.getElementById`, it started/stopped itself with its own IntersectionObserver,
// and it drove the simulation with `setInterval`/`setTimeout`. None of that survives the
// flattening:
//
//   * every lookup is `root.querySelector` — a section module that reaches outside its own
//     section is a defect even when it happens to work, and after flattening the ids it used
//     (`routes`, `direct-path`, `game-status`, …) are generic enough to belong to any of the
//     ten sections on this page. They are all prefixed `tb-mc-` in the markup for the same
//     reason (see ../sections/multiconnection.tsx);
//   * the IntersectionObserver is gone: `useSectionAnimation` already gates this module's mount
//     on the section entering the viewport, which is exactly what the source's observer did;
//   * `setInterval`/`setTimeout`/`performance.now()` are gone: every deadline is expressed in
//     the elapsed-ms value `clock` hands out each frame. That buys three things the timers did
//     not — teardown can never leave a timer behind, a hidden tab freezes the simulation for
//     free (rAF does not fire), and prefers-reduced-motion arrives as a single `Infinity` frame
//     (engine.ts's contract) which maps onto the source's own `reduced` branch, `applyReduced`.
//   * the source's `visibilitychange` start/stop pair is dropped for that same reason — with a
//     rAF-driven clock a hidden tab already stops advancing, so the listener had nothing left
//     to do. `instrument.dataset.motion` still reports `running` / `static`, but never `paused`.
//
// Copy split, matching hero.tsx/hero.ts: the static no-JS state is page copy and comes from
// `t(...)` in the section; everything this module writes at runtime comes from
// `strings.multiConnectionInstrument`. The source assembled its coloured event sentences with
// `innerHTML`; those strings are plain text here and the spans are built as real nodes.
import type { MultiConnectionEventPart } from './strings';
import type { SectionModule } from './use-section-animation';

export interface BridgeOption {
  name: string;
  access: number;
  edge: number;
}

// Source 2748-2754 (bridge pools) and 2726-2730 (the two packet delays each route renders with).
// Exported because ../sections/multiconnection.tsx renders the resting frame of this same data:
// route 0's markup is pool[0], which is what the simulation starts from.
export const MULTICONNECTION_ROUTES = [
  {
    pool: [
      { name: 'Washington 105', access: 19, edge: 2 },
      { name: 'Washington 114', access: 19, edge: 2 },
      { name: 'Washington 103', access: 18, edge: 2 },
    ],
    packetDelays: ['-.2s', '-1s'],
  },
  {
    pool: [
      { name: 'Dallas 107', access: 1, edge: 18 },
      { name: 'Dallas 114', access: 2, edge: 19 },
      { name: 'Dallas 105', access: 1, edge: 19 },
    ],
    packetDelays: ['-.45s', '-1.2s'],
  },
  {
    pool: [
      { name: 'Dallas 121', access: 2, edge: 19 },
      { name: 'Dallas 105', access: 1, edge: 19 },
      { name: 'Dallas 118', access: 2, edge: 19 },
    ],
    packetDelays: ['-.7s', '-1.5s'],
  },
  {
    pool: [
      { name: 'Washington 102', access: 19, edge: 2 },
      { name: 'Washington 121', access: 19, edge: 1 },
      { name: 'Washington 108', access: 18, edge: 2 },
    ],
    packetDelays: ['-.9s', '-1.7s'],
  },
  {
    pool: [
      { name: 'Dallas 114', access: 2, edge: 20 },
      { name: 'Dallas 103', access: 2, edge: 19 },
      { name: 'Dallas 109', access: 1, edge: 20 },
    ],
    packetDelays: ['-1.1s', '-1.9s'],
  },
] as const;

// Source 2756. `weight` splits the ISP total across the seven hops; the last hop absorbs the
// rounding remainder so the hops always add up to the number the metric shows.
export const MULTICONNECTION_ISP_HOPS = [
  { name: 'Dallas', weight: 0.1 },
  { name: 'Kansas City', weight: 0.16 },
  { name: 'Davenport', weight: 0.13 },
  { name: 'Chicago', weight: 0.16 },
  { name: 'Columbus', weight: 0.15 },
  { name: 'Pittsburgh', weight: 0.15 },
  { name: 'Washington', weight: 0.15 },
] as const;

// Source 2757-2758.
const DIRECT_PING_PATTERN = [80, 95, 150, 98, 210, 116, 184, 87, 198, 132, 162, 83, 205, 109, 176, 92];
const FAILURE_ORDER = [0, 2, 4, 1, 3];

const METRIC_INTERVAL_MS = 620;
const FIRST_FAILURE_WAIT_MS = 3000;
const FAILURE_WAIT_MS = 2350;
const FAILURE_WAIT_JITTER_MS = 550;
const RECOVERY_MS = 2200;
const RECOVERY_JITTER_MS = 700;
const COUNTDOWN_INTERVAL_MS = 180;
const ISP_DISCONNECT_PERIOD = 13;
const ISP_DISCONNECT_PHASE = 8;
const ISP_DISCONNECT_TICKS = 4;
const ROUTE_COUNT = MULTICONNECTION_ROUTES.length;

interface RouteState {
  index: number;
  pool: readonly BridgeOption[];
  element: HTMLElement;
  bridgeName: HTMLElement;
  bridgeState: HTMLElement;
  accessValue: HTMLElement;
  edgeValue: HTMLElement;
  totalValue: HTMLElement;
  variant: number;
  name: string;
  baseAccess: number;
  baseEdge: number;
  access: number;
  edge: number;
  total: number;
  online: boolean;
  recoverAt: number;
  oldName: string;
  nextName: string;
}

interface HopState {
  name: string;
  weight: number;
  latency: number;
  element: HTMLElement;
  value: HTMLElement;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function renderEventParts(target: HTMLElement, parts: MultiConnectionEventPart[]): void {
  const node = target;
  node.textContent = '';
  for (const part of parts) {
    if (part.tone) {
      const span = document.createElement('span');
      span.className = part.tone;
      span.textContent = part.text;
      node.appendChild(span);
    } else {
      node.appendChild(document.createTextNode(part.text));
    }
  }
}

function legendItem(label: string, offline: boolean): HTMLElement {
  const item = document.createElement('span');
  if (offline) item.className = 'offline';
  item.appendChild(document.createElement('i'));
  item.appendChild(document.createTextNode(label));
  return item;
}

function strongNode(text: string): HTMLElement {
  const strong = document.createElement('b');
  strong.textContent = text;
  return strong;
}

function separatorNode(text: string): HTMLElement {
  const separator = document.createElement('span');
  separator.setAttribute('aria-hidden', 'true');
  separator.textContent = text;
  return separator;
}

export const multiconnectionAnimation: SectionModule = ({ root, strings, clock }) => {
  const copy = strings.multiConnectionInstrument;

  const instrument = root.querySelector<HTMLElement>('#tb-mc-instrument');
  const topology = root.querySelector<HTMLElement>('#tb-mc-topology');
  const routesContainer = root.querySelector<HTMLElement>('#tb-mc-routes');
  const hubNode = root.querySelector<HTMLElement>('.node--hub');
  const directPath = root.querySelector<HTMLElement>('#tb-mc-direct-path');
  const metricWithout = root.querySelector<HTMLElement>('.metric--without');
  const metricWith = root.querySelector<HTMLElement>('.metric--with');
  const modeToggle = root.querySelector<HTMLElement>('#tb-mc-toggle');
  const modeDescription = root.querySelector<HTMLElement>('#tb-mc-mode-description');
  const flowMiddle = root.querySelector<HTMLElement>('#tb-mc-flow-middle');
  const activeCount = root.querySelector<HTMLElement>('#tb-mc-active-count');
  const withoutPing = root.querySelector<HTMLElement>('#tb-mc-without-ping');
  const withoutLoss = root.querySelector<HTMLElement>('#tb-mc-without-loss');
  const withoutJitter = root.querySelector<HTMLElement>('#tb-mc-without-jitter');
  const withPing = root.querySelector<HTMLElement>('#tb-mc-with-ping');
  const withLoss = root.querySelector<HTMLElement>('#tb-mc-with-loss');
  const withJitter = root.querySelector<HTMLElement>('#tb-mc-with-jitter');
  const eventKicker = root.querySelector<HTMLElement>('#tb-mc-event-kicker');
  const eventMessage = root.querySelector<HTMLElement>('#tb-mc-event-message');
  const lockCopy = root.querySelector<HTMLElement>('#tb-mc-lock-copy');
  const lockReadout = root.querySelector<HTMLElement>('#tb-mc-lock-readout');
  const modeLegend = root.querySelector<HTMLElement>('#tb-mc-legend');
  const uptimeCopy = root.querySelector<HTMLElement>('#tb-mc-uptime');
  const gameHopEl = root.querySelector<HTMLElement>('#tb-mc-game-hop');
  const gameNode = root.querySelector<HTMLElement>('.node--game');
  const gameStatus = root.querySelector<HTMLElement>('#tb-mc-game-status');
  const gameplayVideo = root.querySelector<HTMLVideoElement>('.node--game video');

  if (!instrument || !topology || !routesContainer || !hubNode || !directPath) return () => {};
  if (!metricWithout || !metricWith || !modeToggle || !modeDescription || !flowMiddle || !activeCount) return () => {};
  if (!withoutPing || !withoutLoss || !withoutJitter || !withPing || !withLoss || !withJitter) return () => {};
  if (!eventKicker || !eventMessage || !lockCopy || !lockReadout || !modeLegend || !uptimeCopy) return () => {};
  if (!gameHopEl || !gameNode || !gameStatus) return () => {};

  const routes: RouteState[] = [];
  root.querySelectorAll<HTMLElement>('.route').forEach((element, index) => {
    const seed = MULTICONNECTION_ROUTES[index];
    const bridgeName = element.querySelector<HTMLElement>('.bridge-name');
    const bridgeState = element.querySelector<HTMLElement>('.bridge-state');
    const accessValue = element.querySelector<HTMLElement>('.access-ms');
    const edgeValue = element.querySelector<HTMLElement>('.edge-ms');
    const totalValue = element.querySelector<HTMLElement>('.total-ms');
    if (!seed || !bridgeName || !bridgeState || !accessValue || !edgeValue || !totalValue) return;
    const first = seed.pool[0];
    routes.push({
      index,
      pool: seed.pool,
      element,
      bridgeName,
      bridgeState,
      accessValue,
      edgeValue,
      totalValue,
      variant: 0,
      name: first.name,
      baseAccess: first.access,
      baseEdge: first.edge,
      access: first.access,
      edge: first.edge,
      total: first.access + first.edge,
      online: true,
      recoverAt: 0,
      oldName: '',
      nextName: '',
    });
  });

  const hops: HopState[] = [];
  root.querySelectorAll<HTMLElement>('.isp-hop').forEach((element, index) => {
    const seed = MULTICONNECTION_ISP_HOPS[index];
    const value = element.querySelector<HTMLElement>('.hop-value');
    if (!seed || !value) return;
    hops.push({ name: seed.name, weight: seed.weight, latency: 0, element, value });
  });

  if (routes.length !== ROUTE_COUNT || hops.length !== MULTICONNECTION_ISP_HOPS.length) return () => {};

  let metricTick = 0;
  let gameHop = 0;
  let lastWith = 19;
  let lastWithout = 168;
  let nopingEnabled = true;
  let ispTick = 0;
  let ispOfflineIndex = -1;
  let ispOfflineTicks = 0;
  let ispCurrentTotal = 168;
  let failureCursor = 0;
  let running = false;
  let nextMetricAt = 0;
  let nextFailureAt = FIRST_FAILURE_WAIT_MS;
  let nextCountdownAt = Infinity;
  let now = 0;

  function setMetricValue(element: HTMLElement, value: number): void {
    const first = element.firstChild;
    if (!first || first.nodeType !== Node.TEXT_NODE) return;
    if (parseInt(first.nodeValue ?? '', 10) === value) return;
    first.nodeValue = String(value);
    element.classList.remove('bump');
    // Reading a layout box flushes the class removal, so re-adding it restarts the keyframe
    // instead of being coalesced away — the source did this with `void el.offsetWidth`.
    element.getBoundingClientRect();
    element.classList.add('bump');
  }

  function onlineRoutes(): RouteState[] {
    return routes.filter((route) => route.online);
  }

  function pickBest(): RouteState {
    const online = onlineRoutes();
    return online.reduce((best, route) => (route.total < best.total ? route : best), online[0] ?? routes[0]!);
  }

  function stateLabel(route: RouteState): string {
    if (route.online) return copy.bridgeStateConnected;
    return copy.bridgeStateSwapping(Math.max(0, (route.recoverAt - now) / 1000).toFixed(1));
  }

  function renderRoutes(): void {
    const best = pickBest();
    for (const route of routes) {
      route.element.classList.toggle('is-offline', !route.online);
      route.element.style.setProperty('--speed', `${(1.28 + route.total * 0.017).toFixed(2)}s`);
      route.bridgeName.textContent = route.name;
      route.bridgeState.textContent = stateLabel(route);
      route.accessValue.textContent = route.online ? String(route.access) : '—';
      route.edgeValue.textContent = route.online ? String(route.edge) : '—';
      route.totalValue.textContent = route.online ? String(route.total) : '—';
      route.element.setAttribute('aria-label', route.online ? copy.routeLabel(route.name, route.access, route.edge, route.total) : copy.routeSwappingLabel(route.oldName, route.nextName));
    }
    if (nopingEnabled) {
      activeCount!.textContent = copy.activeCountBridges(onlineRoutes().length);
      lockReadout!.textContent = copy.lockReadoutBridge(best.name, best.total);
    }
    gameHopEl!.textContent = `${gameHop} ms`;
    const currentWith = best.total + gameHop;
    setMetricValue(withPing!, currentWith);
    withJitter!.textContent = `${Math.max(1, Math.abs(currentWith - lastWith) + 1)}ms`;
    withLoss!.textContent = '0.0%';
    lastWith = currentWith;
  }

  function calculateIspHopLatencies(targetTotal: number): number {
    let allocated = 0;
    hops.forEach((hop, index) => {
      const state = hop;
      state.latency = index === hops.length - 1 ? targetTotal - allocated : Math.max(1, Math.round(targetTotal * state.weight));
      allocated += state.latency;
    });
    return hops.reduce((sum, hop) => sum + hop.latency, 0);
  }

  function renderIspHops(spikeIndex: number): void {
    hops.forEach((hop, index) => {
      const state = hop;
      const offline = index === ispOfflineIndex;
      state.element.classList.toggle('is-offline', offline);
      state.element.classList.toggle('is-spiking', !offline && index === spikeIndex);
      state.value.textContent = offline ? copy.hopOffline : `${state.latency}ms`;
      state.element.dataset.latency = String(state.latency);
      state.element.setAttribute('aria-label', offline ? copy.hopOfflineLabel(state.name) : copy.hopLabel(state.name, state.latency));
    });
  }

  function syncGameplay(): void {
    if (!gameplayVideo) return;
    const frozen = !nopingEnabled && ispOfflineIndex >= 0;
    gameNode!.classList.toggle('is-frozen', frozen);
    let status = copy.gameDegraded;
    if (nopingEnabled) status = copy.gameLive;
    else if (frozen) status = copy.gameOffline;
    gameStatus!.textContent = status;
    gameplayVideo.playbackRate = nopingEnabled ? 1 : 0.38;
    let label = copy.gameplaySlowedLabel;
    if (frozen) label = copy.gameplayFrozenLabel;
    else if (nopingEnabled) label = copy.gameplayLabel;
    gameplayVideo.setAttribute('aria-label', label);
    if (frozen || !running) {
      gameplayVideo.pause();
      return;
    }
    // A rejected play() is the browser's autoplay policy talking, not a fault of ours; the
    // source swallowed it the same way. Nothing else in the section depends on the video.
    gameplayVideo.play()?.catch(() => {});
  }

  function updateIspPath(total: number, previous: number): void {
    ispTick += 1;
    let justDisconnected = false;
    let recovered = false;
    if (ispOfflineIndex >= 0) {
      ispOfflineTicks -= 1;
      if (ispOfflineTicks <= 0) {
        ispOfflineIndex = -1;
        recovered = true;
      }
    } else if (ispTick % ISP_DISCONNECT_PERIOD === ISP_DISCONNECT_PHASE) {
      ispOfflineIndex = 1 + (Math.floor(ispTick / ISP_DISCONNECT_PERIOD) % 5);
      ispOfflineTicks = ISP_DISCONNECT_TICKS;
      justDisconnected = true;
    }
    const disconnected = ispOfflineIndex >= 0;
    const spike = !disconnected && total > previous;
    const spikeIndex = spike ? ispTick % hops.length : -1;
    renderIspHops(spikeIndex);
    directPath!.classList.toggle('is-disconnected', disconnected);
    directPath!.classList.toggle('has-spike', spike);
    lockCopy!.textContent = copy.lockCopyIsp;
    if (disconnected) {
      activeCount!.textContent = copy.activeCountLost;
      lockReadout!.textContent = copy.lockReadoutLost;
      withoutLoss!.textContent = '100%';
      const offlineHop = hops[ispOfflineIndex];
      if (justDisconnected && offlineHop) renderEventParts(eventMessage!, copy.ispDisconnected(offlineHop.name));
    } else {
      activeCount!.textContent = copy.activeCountIsp;
      lockReadout!.textContent = copy.lockReadoutIsp(total);
      const spikeHop = hops[spikeIndex];
      if (recovered) renderEventParts(eventMessage!, copy.ispRestored());
      else if (spike && spikeHop) renderEventParts(eventMessage!, copy.ispSpike(total - previous, spikeHop.name));
    }
    syncGameplay();
  }

  function setNoPingMode(enabled: boolean): void {
    nopingEnabled = enabled;
    instrument!.classList.toggle('is-isp-mode', !enabled);
    topology!.classList.toggle('is-direct', !enabled);
    topology!.setAttribute('aria-label', enabled ? copy.topologyLabelNoping : copy.topologyLabelIsp);
    directPath!.setAttribute('aria-hidden', enabled ? 'true' : 'false');
    routesContainer!.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    hubNode!.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    modeToggle!.setAttribute('aria-checked', enabled ? 'true' : 'false');
    metricWith!.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    metricWithout!.setAttribute('aria-pressed', enabled ? 'false' : 'true');
    flowMiddle!.textContent = enabled ? copy.flowMiddleBridges : copy.flowMiddleIsp;

    for (const route of routes) {
      route.online = true;
      route.recoverAt = 0;
    }
    nextCountdownAt = Infinity;
    renderRoutes();

    modeLegend!.textContent = '';
    if (enabled) {
      modeDescription!.textContent = copy.modeDescriptionNoping;
      eventKicker!.textContent = copy.eventKickerNoping;
      eventMessage!.textContent = copy.eventMessageNoping;
      lockCopy!.textContent = copy.lockCopyNoping;
      modeLegend!.appendChild(legendItem(copy.legendActiveRoute, false));
      modeLegend!.appendChild(legendItem(copy.legendReplacingBridge, true));
      uptimeCopy!.textContent = copy.uptimeNoping.lead;
      uptimeCopy!.appendChild(strongNode(copy.uptimeNoping.strong));
      uptimeCopy!.appendChild(document.createTextNode(' '));
      uptimeCopy!.appendChild(separatorNode(copy.uptimeNoping.separator));
      uptimeCopy!.appendChild(document.createTextNode(copy.uptimeNoping.middle));
      uptimeCopy!.appendChild(strongNode(copy.uptimeNoping.strongTail));
      directPath!.classList.remove('is-disconnected', 'has-spike');
      ispOfflineIndex = -1;
      ispOfflineTicks = 0;
      // The source rescheduled its failure timer here; the elapsed-ms deadline is the same idea.
      nextFailureAt = now + (failureCursor === 0 ? FIRST_FAILURE_WAIT_MS : FAILURE_WAIT_MS + Math.round(Math.random() * FAILURE_WAIT_JITTER_MS));
    } else {
      modeDescription!.textContent = copy.modeDescriptionIsp;
      eventKicker!.textContent = copy.eventKickerIsp;
      eventMessage!.textContent = copy.eventMessageIsp;
      lockCopy!.textContent = copy.lockCopyIsp;
      lockReadout!.textContent = copy.lockReadoutIsp(ispCurrentTotal);
      activeCount!.textContent = copy.activeCountIsp;
      modeLegend!.appendChild(legendItem(copy.legendSingleIspPath, false));
      modeLegend!.appendChild(legendItem(copy.legendOfflineHop, true));
      uptimeCopy!.textContent = '';
      uptimeCopy!.appendChild(strongNode(copy.uptimeIsp.strongLead));
      uptimeCopy!.appendChild(document.createTextNode(' '));
      uptimeCopy!.appendChild(separatorNode(copy.uptimeIsp.separator));
      uptimeCopy!.appendChild(document.createTextNode(copy.uptimeIsp.tail));
      ispTick = 0;
      ispOfflineIndex = -1;
      ispOfflineTicks = 0;
      calculateIspHopLatencies(ispCurrentTotal);
      renderIspHops(-1);
    }
    syncGameplay();
  }

  function refreshLatencies(): void {
    metricTick += 1;
    for (const route of routes.filter((candidate) => candidate.online)) {
      const isDallas = route.name.indexOf('Dallas') === 0;
      const accessWave = Math.sin(metricTick * 0.55 + route.index * 1.6);
      const edgeWave = Math.sin(metricTick * 0.82 + route.index);
      let accessNoise = 0;
      if (accessWave > 0.68) accessNoise = 1;
      else if (accessWave < -0.68) accessNoise = -1;
      let edgeNoise = 0;
      if (edgeWave > 0.78) edgeNoise = 1;
      else if (edgeWave < -0.78) edgeNoise = -1;
      if (isDallas) {
        route.access = clamp(route.baseAccess + accessNoise, 1, 2);
        route.edge = clamp(route.baseEdge + edgeNoise, 18, 20);
      } else {
        route.access = clamp(route.baseAccess + accessNoise, 18, 19);
        route.edge = clamp(route.baseEdge + edgeNoise, 1, 2);
      }
      route.total = route.access + route.edge;
    }
    gameHop = 0;
    const targetTotal = DIRECT_PING_PATTERN[(metricTick - 1) % DIRECT_PING_PATTERN.length] ?? lastWithout;
    const direct = calculateIspHopLatencies(targetTotal);
    const previousDirect = lastWithout;
    const pingSwing = Math.abs(direct - previousDirect);
    const liveJitter = clamp(Math.round(24 + pingSwing * 0.42), 30, 78);
    ispCurrentTotal = direct;
    setMetricValue(withoutPing!, direct);
    withoutLoss!.textContent = `${(3.5 + liveJitter * 0.035 + (Math.sin(metricTick * 0.47) + 1) * 0.2).toFixed(1)}%`;
    withoutJitter!.textContent = `${liveJitter}ms`;
    renderRoutes();
    if (!nopingEnabled) updateIspPath(direct, previousDirect);
    lastWithout = direct;
  }

  function failRoute(index: number): void {
    const route = routes[index];
    if (!route || !nopingEnabled || !route.online || onlineRoutes().length <= 1) return;
    route.oldName = route.name;
    route.variant = (route.variant + 1) % route.pool.length;
    const next = route.pool[route.variant];
    if (!next) return;
    route.nextName = next.name;
    route.online = false;
    route.recoverAt = now + RECOVERY_MS + Math.round(Math.random() * RECOVERY_JITTER_MS);
    renderEventParts(eventMessage!, copy.routeSwapped(route.oldName, next.name, onlineRoutes().length));
    renderRoutes();
    nextCountdownAt = now + COUNTDOWN_INTERVAL_MS;
  }

  function recoverRoute(route: RouteState): void {
    const state = route;
    const next = state.pool[state.variant];
    if (!next) return;
    state.name = next.name;
    state.baseAccess = next.access;
    state.baseEdge = next.edge;
    state.access = next.access;
    state.edge = next.edge;
    state.total = next.access + next.edge;
    state.online = true;
    state.recoverAt = 0;
    if (nopingEnabled) {
      const stillOffline = routes.find((candidate) => !candidate.online);
      renderEventParts(eventMessage!, stillOffline ? copy.routeRestoredWhileSwapping(state.name, onlineRoutes().length, stillOffline.oldName, stillOffline.nextName) : copy.routeRestored(state.name));
    }
    renderRoutes();
  }

  function applyReduced(): void {
    for (const route of routes) route.online = true;
    gameHop = 0;
    renderRoutes();
    instrument!.dataset.motion = 'static';
    syncGameplay();
    eventMessage!.textContent = nopingEnabled ? copy.reducedMotionNoping : copy.reducedMotionIsp;
  }

  function step(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs)) {
      applyReduced();
      return;
    }
    now = elapsedMs;
    if (!running) {
      running = true;
      instrument!.dataset.motion = 'running';
      syncGameplay();
      nextMetricAt = elapsedMs;
      nextFailureAt = elapsedMs + FIRST_FAILURE_WAIT_MS;
    }
    if (elapsedMs >= nextMetricAt) {
      refreshLatencies();
      // Deadline is re-based on `now` rather than incremented, so a long stall (a backgrounded
      // tab, a slow frame) replays one tick instead of a burst of catch-up ticks.
      nextMetricAt = elapsedMs + METRIC_INTERVAL_MS;
    }
    if (nopingEnabled && elapsedMs >= nextFailureAt) {
      const index = FAILURE_ORDER[failureCursor % FAILURE_ORDER.length] ?? 0;
      failureCursor += 1;
      const candidate = routes[index];
      if (candidate?.online && onlineRoutes().length > 1) failRoute(index);
      nextFailureAt = elapsedMs + FAILURE_WAIT_MS + Math.round(Math.random() * FAILURE_WAIT_JITTER_MS);
    }
    const offline = routes.filter((route) => !route.online);
    for (const route of offline) {
      if (elapsedMs >= route.recoverAt) recoverRoute(route);
    }
    if (routes.some((route) => !route.online)) {
      // The source ran a 180ms interval while a route was swapping, only so the "swapping 1.4s"
      // countdown kept ticking; re-rendering every frame instead would be 60 full route repaints
      // a second for a label that changes ten times a second.
      if (elapsedMs >= nextCountdownAt) {
        renderRoutes();
        nextCountdownAt = elapsedMs + COUNTDOWN_INTERVAL_MS;
      }
    } else {
      nextCountdownAt = Infinity;
    }
  }

  const listeners = new AbortController();
  const { signal } = listeners;

  function bindModeChoice(element: HTMLElement, enabled: boolean): void {
    element.addEventListener('click', () => setNoPingMode(enabled), { signal });
    element.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setNoPingMode(enabled);
      },
      { signal }
    );
  }

  modeToggle.addEventListener('click', () => setNoPingMode(!nopingEnabled), { signal });
  bindModeChoice(metricWithout, false);
  bindModeChoice(metricWith, true);
  setNoPingMode(true);

  const unsubscribe = clock.subscribe(step);

  return () => {
    unsubscribe();
    listeners.abort();
    running = false;
    gameplayVideo?.pause();
    delete instrument.dataset.motion;
  };
};
